import React, { useState } from 'react';
import { Layers, Mountain, BookOpen, ChevronRight, Home, Users } from 'lucide-react';
import { GibbHighlandConnectionSection } from './gibbs/GibbHighlandConnectionSection';
import { GibbNewZealandSection } from './gibbs/GibbNewZealandSection';

export interface GibbPageItem {
  id: string;
  pageNumber: number;
  title: string;
  subtitle: string;
}

export const GIBB_PAGES: GibbPageItem[] = [
  { 
    id: 'page1', 
    pageNumber: 1, 
    title: 'Our Highland Connection', 
    subtitle: 'Glen Clova & Glen Esk, Clan Lindsays & Gibbs, Voyages of Matoaka & Accrington, Teviotdale' 
  },
  { 
    id: 'page2', 
    pageNumber: 2, 
    title: 'Helen Gibb (1839-1914) The Queen of Cabbage Tree Flat', 
    subtitle: 'Cabbage Tree Flat Accommodation House, Greta Valley, Descendants & The Rev George Lindsay' 
  },
];

export const GibbsSection: React.FC = () => {
  const [activePageId, setActivePageId] = useState<string>('page1');

  const currentPage = GIBB_PAGES.find(p => p.id === activePageId) || GIBB_PAGES[0];

  const handleSelectPage = (pageId: string) => {
    if (pageId === 'hub') {
      setActivePageId('all');
    } else {
      setActivePageId(pageId);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-16 font-sans text-[#2c221e]">
      
      {/* 2-Page Series Navigation Bar */}
      <nav aria-label="Gibb Family Navigation" className="bg-[#f3ede0] p-3 sm:p-4 rounded-2xl border-2 border-[#d8ceb9] shadow-xs">
        <div className="flex items-center justify-between gap-2 pb-2.5 mb-2.5 border-b border-[#d8ceb9] text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider">
            <Mountain className="w-4 h-4 text-[#7a5b3a]" />
            <span>The Gibb Family: 2-Page Historical Series</span>
          </div>
          <button
            onClick={() => setActivePageId(activePageId === 'all' ? 'page1' : 'all')}
            className="text-[11px] font-bold text-[#7a5b3a] hover:underline bg-[#e8dec9] px-2.5 py-1 rounded-lg transition cursor-pointer"
          >
            {activePageId === 'all' ? 'View Single Page' : 'View All Pages'}
          </button>
        </div>

        {/* Horizontal Page Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {GIBB_PAGES.map((page) => {
            const isSelected = activePageId === page.id;
            return (
              <button
                key={page.id}
                onClick={() => {
                  setActivePageId(page.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`whitespace-nowrap px-3.5 py-2 rounded-xl text-xs font-serif font-bold transition flex items-center gap-2 shrink-0 border cursor-pointer ${
                  isSelected
                    ? 'bg-[#7a5b3a] text-white border-[#7a5b3a] shadow-xs'
                    : 'bg-[#fbf9f4] text-[#4a3525] border-[#d8ceb9] hover:bg-[#e8dec9]'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${isSelected ? 'bg-amber-200 text-[#2c221e]' : 'bg-[#e8dec9] text-[#7a5b3a]'}`}>
                  {page.pageNumber}
                </span>
                <span>Page {page.pageNumber}: {page.title}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* RENDER ACTIVE PAGE */}
      {activePageId === 'page1' && (
        <GibbHighlandConnectionSection onSelectPage={handleSelectPage} />
      )}

      {activePageId === 'page2' && (
        <GibbNewZealandSection onSelectPage={handleSelectPage} />
      )}

      {/* ALL PAGES VIEW */}
      {activePageId === 'all' && (
        <div className="space-y-12">
          {/* Chapter Index Hub Cards */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-6">
            <div>
              <h2 className="font-serif text-2xl font-bold text-[#2c221e]">
                The Gibb Family: Table of Contents
              </h2>
              <p className="text-sm text-[#7a5b3a] mt-1">
                Select a chapter below to read individual sections or scroll down to view all chapters.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {GIBB_PAGES.map((page) => (
                <div
                  key={page.id}
                  onClick={() => {
                    setActivePageId(page.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-[#fbf9f4] p-5 rounded-2xl border border-[#d8ceb9] hover:border-[#7a5b3a] transition cursor-pointer space-y-2 group shadow-xs hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-serif font-bold uppercase tracking-wider text-[#7a5b3a] bg-[#e8dec9] px-2.5 py-0.5 rounded-full">
                      Page {page.pageNumber} of 2
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#d8ceb9] group-hover:text-[#7a5b3a] transition-transform group-hover:translate-x-1" />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-[#2c221e] group-hover:text-[#7a5b3a] transition-colors">
                    {page.title}
                  </h3>
                  <p className="text-xs text-[#5c4328] line-clamp-2">
                    {page.subtitle}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <GibbHighlandConnectionSection onSelectPage={handleSelectPage} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <GibbNewZealandSection onSelectPage={handleSelectPage} />
        </div>
      )}

    </div>
  );
};
